import debounce from 'lodash.debounce';
import { createContext, ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useDebugLogging } from '../utils';

type KeepAlive = () => void;
type TimeoutContextValue = {
  isAlive: boolean;
  setTrackingEnabled: (val: boolean) => void;
};

const TimeoutContext = createContext<TimeoutContextValue>({
  isAlive: true,
  setTrackingEnabled: () => undefined,
});

const DEBOUNCE_INTERVAL = 2000;
const SECONDS_PER_MINUTE = 60;
const MILLIS_PER_SECOND = 1000;
const DEFAULT_INACTIVITY_IN_MINUTES = 15;

const userEvents = ['mousemove', 'keydown', 'click', 'touchmove'];

const setUserActivityListeners = ({
  keepAlive,
  method,
}: {
  keepAlive: KeepAlive;
  method: 'addEventListener' | 'removeEventListener';
}) => {
  for (const eventName of userEvents) {
    globalThis[method](eventName, keepAlive);
  }
};

const syncUserActivityTracking = ({
  isMounted,
  keepAlive,
  trackingActive,
}: {
  isMounted: boolean;
  keepAlive: KeepAlive;
  trackingActive: boolean;
}) => {
  if (trackingActive && !isMounted) {
    setUserActivityListeners({ keepAlive, method: 'addEventListener' });
    return true;
  }

  if (!trackingActive && isMounted) {
    setUserActivityListeners({ keepAlive, method: 'removeEventListener' });
    return false;
  }

  return isMounted;
};

const useUserActivityTracker = (keepAlive: KeepAlive, trackingActive: boolean) => {
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = syncUserActivityTracking({ isMounted: mounted.current, keepAlive, trackingActive });

    const removeEventListeners = () => {
      mounted.current = false;
      setUserActivityListeners({ keepAlive, method: 'removeEventListener' });
    };

    return removeEventListeners;
  }, [keepAlive, trackingActive]);
};

const useKeepAliveFunction = (onTimeout: () => void, trackingEnabled: boolean, userTimeoutInMinutes: number) => {
  const debugLog = useDebugLogging();
  const sessionTimeoutInMillis = userTimeoutInMinutes * MILLIS_PER_SECOND * SECONDS_PER_MINUTE;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const resetTimeout = useCallback(() => {
    const ref = timeoutRef.current;
    const cancelTimeout = () => {
      clearTimeout(ref);
    };
    if (ref) cancelTimeout();

    if (trackingEnabled) {
      debugLog('keep alive');
      timeoutRef.current = setTimeout(onTimeout, sessionTimeoutInMillis);
    }
    return cancelTimeout;
  }, [onTimeout, trackingEnabled, sessionTimeoutInMillis, debugLog]);

  useEffect(() => {
    const ref = timeoutRef.current;
    resetTimeout();
    return () => {
      if (ref) clearTimeout(ref);
    };
  }, [resetTimeout]);

  const keepAlive = useMemo(
    () => debounce(resetTimeout, DEBOUNCE_INTERVAL, { leading: true, maxWait: DEBOUNCE_INTERVAL }),
    [resetTimeout]
  );
  return keepAlive;
};

const TimeoutProvider = ({
  children,
  userTimeoutInMinutes = DEFAULT_INACTIVITY_IN_MINUTES,
}: {
  readonly children: ReactElement;
  readonly userTimeoutInMinutes?: number;
}) => {
  const addToLog = useDebugLogging();
  const [isAlive, setIsAlive] = useState(true);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const onTimeout = useCallback(() => {
    addToLog('activity timeout');
    setIsAlive(false);
  }, [addToLog]);
  const keepAlive = useKeepAliveFunction(onTimeout, trackingEnabled, userTimeoutInMinutes);
  useUserActivityTracker(keepAlive, trackingEnabled);

  const value = useMemo(
    () => ({
      isAlive,
      setTrackingEnabled,
    }),
    [isAlive, setTrackingEnabled]
  );
  return <TimeoutContext.Provider value={value}>{children}</TimeoutContext.Provider>;
};

const useTimeoutContext = () => useContext(TimeoutContext);

const useIsAlive = () => useTimeoutContext().isAlive;

export { syncUserActivityTracking, TimeoutProvider, useIsAlive, useTimeoutContext };
